import { useEffect } from 'react';
import { usePrevious } from 'react-use';

import {
  useAppointmentId,
  useIsUserAppointmentOwner,
} from '@eluve/frontend-appointment-hooks';
import {
  useFeatureFlagPayload,
  useIsPostHogFeatureFlagEnabled,
} from '@eluve/posthog-react';
import { useTenantIdFromParams } from '@eluve/session-helpers';
import { useAppointmentTemplates } from '@eluve/smart-blocks';

import { OutputTemplateSelector } from './OutputTemplateSelector';
import { PromptAndOutputTemplateSelector } from './PromptAndOutputTemplateSelector';

interface TemplateSelectorGuardProps {
  selectedPromptTemplateId?: string;
  selectedOutputTemplateId?: string;
  onSelectionChanged: (data: {
    initialPromptTemplateId: string;
    initialOutputTemplateId: string | null;
  }) => void;
  usesPortal?: boolean;
  side?: 'bottom' | 'left' | 'right' | 'top';
}

export const TemplateSelectorGuard: React.FC<TemplateSelectorGuardProps> = ({
  selectedPromptTemplateId,
  selectedOutputTemplateId,
  onSelectionChanged,
  usesPortal,
  side,
}) => {
  const tenantId = useTenantIdFromParams();
  const appointmentId = useAppointmentId();
  const isUserApptOwner = useIsUserAppointmentOwner();

  const {
    initialLlmOutputTemplateId,
    initialPromptTemplateId,
    updateAppointmentOutputTemplate,
    updateAppointmentPromptTemplate,
  } = useAppointmentTemplates();

  const isMastraFlagEnabled = useIsPostHogFeatureFlagEnabled(
    'USE_MASTRA_FOR_APPOINTMENT_SUMMARY',
  );
  const mastraFlagPayload = useFeatureFlagPayload(
    'USE_MASTRA_FOR_APPOINTMENT_SUMMARY',
  );

  const mastraPromptTemplateId = mastraFlagPayload.promptTemplateId;
  const prevMastraPromptTemplateId = usePrevious(mastraPromptTemplateId);

  // Call updateAppointmentPromptTemplate when mastraPromptTemplateId changes
  useEffect(() => {
    if (
      isUserApptOwner &&
      isMastraFlagEnabled &&
      !initialPromptTemplateId &&
      mastraPromptTemplateId !== null &&
      mastraPromptTemplateId !== prevMastraPromptTemplateId &&
      mastraPromptTemplateId !== initialPromptTemplateId
    ) {
      updateAppointmentPromptTemplate({
        variables: {
          tenantId: tenantId!,
          id: appointmentId,
          initialPromptTemplateId: mastraPromptTemplateId,
        },
      });
    }
  }, [
    isMastraFlagEnabled,
    mastraPromptTemplateId,
    updateAppointmentPromptTemplate,
    tenantId,
    appointmentId,
    isUserApptOwner,
    initialPromptTemplateId,
    prevMastraPromptTemplateId,
  ]);

  if (isMastraFlagEnabled) {
    return (
      <OutputTemplateSelector
        selectedOutputTemplateId={initialLlmOutputTemplateId ?? undefined}
        onSelectionChanged={(initialOutputTemplateId) =>
          updateAppointmentOutputTemplate({
            variables: {
              tenantId: tenantId!,
              id: appointmentId,
              initialLlmOutputTemplateId: initialOutputTemplateId,
            },
          })
        }
        usesPortal={usesPortal}
        side={side}
      />
    );
  }

  return (
    <PromptAndOutputTemplateSelector
      selectedPromptTemplateId={selectedPromptTemplateId}
      selectedOutputTemplateId={selectedOutputTemplateId}
      onSelectionChanged={onSelectionChanged}
      usesPortal={usesPortal}
      side={side}
    />
  );
};
